/**
 * TriageBanner — Phase 1.4 four-tile triage queue summary.
 *
 * Extracted from BrandAssetGallery.jsx during May 2026 refactor.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { RotateCw, Sparkles, Copy } from "lucide-react";
import { API, authedFetch, verdictMeta, fmtMins } from "./utils";

function TriageBanner({ workspaceId, onJumpToFilter }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/triage-estimate`
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message || "Could not load triage estimate");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
    // Poll while the backfill is running so the operator sees the
    // distribution fill in. Stops polling once we've classified everything.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <Skeleton
        className="h-20 w-full rounded-lg mb-4"
        data-testid="gallery-triage-banner-loading"
      />
    );
  }
  if (error || !data) {
    // Minimal fallback — the Triage estimate failed, but the operator
    // still needs a discoverable entry point to duplicate triage.
    return (
      <div
        className="mb-6 border border-slate-200 bg-white rounded-lg p-3 flex items-center justify-between flex-wrap gap-2"
        data-testid="gallery-triage-banner-fallback"
      >
        <div className="text-xs text-slate-500">
          Triage queue summary unavailable.
        </div>
        <Link to="/admin/brand-assets/duplicates">
          <Button
            size="sm"
            variant="outline"
            data-testid="gallery-triage-banner-open-duplicates"
          >
            <Copy className="h-3.5 w-3.5 mr-1" /> Find duplicates
          </Button>
        </Link>
      </div>
    );
  }

  const v = data.verdict_distribution || {};
  const safeN = v.safe || 0;
  const ambN = v.ambiguous || 0;
  const unsafeN = v.unsafe || 0;
  const unclN = data.unclassified || 0;
  const total = data.total || 0;
  const reviewMins = data.ambiguous_review_minutes_estimate || 0;

  const tiles = [
    {
      key: "safe",
      label: "Safe to publish",
      count: safeN,
      filter: { classifier_verdict: "safe" },
      vMeta: verdictMeta("safe"),
    },
    {
      key: "ambiguous",
      label: "Needs review",
      count: ambN,
      filter: { classifier_verdict: "ambiguous" },
      vMeta: verdictMeta("ambiguous"),
    },
    {
      key: "unsafe",
      label: "Unsafe",
      count: unsafeN,
      filter: { classifier_verdict: "unsafe" },
      vMeta: verdictMeta("unsafe"),
    },
    {
      key: "unclassified",
      label: "Unclassified",
      count: unclN,
      filter: { classification_state: "unclassified" },
      vMeta: verdictMeta("unclassified"),
    },
  ];

  return (
    <div
      className="mb-6 border border-slate-200 bg-white rounded-lg p-4 sm:p-5"
      data-testid="gallery-triage-banner"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Triage queue
          </h2>
          <p className="text-xs text-slate-500">
            {data.classified.toLocaleString()} of {total.toLocaleString()} classified
            {ambN > 0 ? (
              <>
                {" · "}
                <span className="font-medium text-amber-700">
                  ~{fmtMins(reviewMins)} to clear the review queue
                </span>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ambN > 0 ? (
            <Link to="/admin/brand-assets/triage?filter=ambiguous">
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="gallery-triage-banner-open-mode"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Open triage mode
              </Button>
            </Link>
          ) : null}
          <Link to="/admin/brand-assets/duplicates">
            <Button
              size="sm"
              variant="outline"
              data-testid="gallery-triage-banner-open-duplicates"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Find duplicates
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="gallery-triage-banner-refresh"
          >
            <RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => onJumpToFilter(t.filter)}
            className={`text-left rounded-md border ${t.vMeta.lightBg} border-slate-200
              hover:ring-2 ${t.vMeta.ringColor} transition p-3`}
            data-testid={`gallery-triage-banner-tile-${t.key}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block h-2 w-2 rounded-full ${t.vMeta.color}`} />
              <span className={`text-xs uppercase tracking-wide font-medium ${t.vMeta.textColor}`}>
                {t.label}
              </span>
            </div>
            <div className="text-2xl font-semibold text-slate-900">
              {t.count.toLocaleString()}
            </div>
            <div className="text-[11px] text-slate-500">
              {total > 0 ? `${Math.round((100 * t.count) / total)}%` : "—"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}


export default TriageBanner;
export { TriageBanner };
